"use client";

import Link from "next/link";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Suspense,useDeferredValue, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Factory,
  FilterX,
  Globe2,
  Loader2,
  MapPin,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  TrendingUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  fetchWelcomeSuppliers,
  type WelcomeSupplierCategoryFilter,
  type WelcomeSupplierDirectoryItem,
  type WelcomeSuppliersFilters,
  type WelcomeSupplierRootCategoryFilter,
  type WelcomeSupplierSort,
} from "@/services/welcomeService";

const SUPPLIER_PAGE_SIZE = 18;
const numberFormatter = new Intl.NumberFormat("en-IN");
const EMPTY_FILTERS: WelcomeSuppliersFilters = {
  rootCategories: [],
  categories: [],
};
const SORT_OPTIONS: { value: WelcomeSupplierSort; label: string }[] = [
  { value: "rating_desc", label: "Top rated" },
  { value: "newest", label: "Newest profiles" },
  { value: "oldest", label: "Oldest profiles" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "name_desc", label: "Name Z-A" },
];

const buildRequestKey = ({
  query,
  categoryId,
  verified,
  sort,
}: {
  query: string;
  categoryId: string;
  verified: boolean;
  sort: WelcomeSupplierSort;
}) =>
  JSON.stringify({
    query,
    categoryId,
    verified,
    sort,
  });

const formatCompactCount = (value: number) =>
  numberFormatter.format(Math.max(0, Number(value) || 0));

function SupplierGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={`supplier-skeleton-${index}`}
          className="card-surface overflow-hidden p-5"
        >
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 animate-pulse rounded-2xl bg-muted" />
            <div className="flex-1 space-y-3">
              <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((__, statIndex) => (
              <div
                key={`supplier-skeleton-stat-${statIndex}`}
                className="h-16 animate-pulse rounded-xl bg-muted"
              />
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((__, chipIndex) => (
              <div
                key={`supplier-skeleton-chip-${chipIndex}`}
                className="h-6 w-20 animate-pulse rounded-full bg-muted"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SupplierCard({ item }: { item: WelcomeSupplierDirectoryItem }) {
  return (
    <Link
      href={`/welcome/suppliers/${item.id}`}
      className="card-surface card-hover block overflow-hidden p-5"
    >
      <div className="flex items-start gap-4">
        <div className="relative h-16 w-16 overflow-hidden rounded-2xl border border-border/70 bg-white">
          <Image
            src={item.logo?.trim() || "/s-no-image.png"}
            alt={item.name}
            fill
            sizes="64px"
            className="object-cover"
            onError={(event) => {
              event.currentTarget.src = "/s-no-image.png";
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="line-clamp-1 text-lg font-semibold tracking-tight text-foreground">
              {item.name}
            </h2>
            {item.verified ? (
              <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                <ShieldCheck className="h-3 w-3" />
                Verified
              </Badge>
            ) : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4 text-primary" />
              {item.location || "India"}
            </span>
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 text-amber-500" />
              {item.rating.toFixed(1)}
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4 text-primary" />
              {Math.round(item.responseRate * 100)}% response
            </span>
          </div>

          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {item.mainProducts}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            Products
          </p>
          <p className="mt-1 text-lg font-semibold text-foreground">
            {formatCompactCount(item.productCount)}
          </p>
        </div>
        <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            Member since
          </p>
          <p className="mt-1 text-lg font-semibold text-foreground">
            {item.memberSince || "Recent"}
          </p>
        </div>
        <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            Markets
          </p>
          <p className="mt-1 text-lg font-semibold text-foreground">
            {formatCompactCount(item.preferredDestinationCountries.length)}
          </p>
        </div>
      </div>

      {item.categories.length ? (
        <div className="mt-5">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Categories
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {item.categories.map((category) => (
              <Badge key={`${item.id}-${category}`} variant="secondary">
                {category}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      {item.capabilityTags.length ? (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Capabilities
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {item.capabilityTags.map((tag) => (
              <Badge key={`${item.id}-${tag}`} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4 text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          <Globe2 className="h-4 w-4 text-primary" />
          {item.preferredDestinationCountries.length
            ? item.preferredDestinationCountries.join(", ")
            : "Global trade ready"}
        </span>
        <span className="inline-flex items-center gap-1 font-medium text-primary">
          View profile
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

function WelcomeSuppliersPageContent() {
  const searchParams = useSearchParams();
  const queryFromUrl = searchParams.get("q")?.trim() ?? "";
  const verifiedFromUrl = searchParams.get("verified") === "true";
  const [searchInput, setSearchInput] = useState(queryFromUrl);
  const deferredQuery = useDeferredValue(searchInput.trim());
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(verifiedFromUrl);
  const [sort, setSort] = useState<WelcomeSupplierSort>("rating_desc");
  const [items, setItems] = useState<WelcomeSupplierDirectoryItem[]>([]);
  const [filters, setFilters] = useState<WelcomeSuppliersFilters>(EMPTY_FILTERS);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const latestRequestKeyRef = useRef("");

  useEffect(() => {
    setSearchInput(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    setVerifiedOnly(verifiedFromUrl);
  }, [verifiedFromUrl]);

  useEffect(() => {
    let isActive = true;

    const requestKey = buildRequestKey({
      query: deferredQuery,
      categoryId: selectedCategoryId,
      verified: verifiedOnly,
      sort,
    });

    latestRequestKeyRef.current = requestKey;
    setIsInitialLoading(true);
    setIsLoadingMore(false);
    setError(null);
    setItems([]);
    setNextCursor(null);
    setHasMore(false);

    const loadSuppliers = async () => {
      try {
        const payload = await fetchWelcomeSuppliers({
          query: deferredQuery,
          categoryId: selectedCategoryId || null,
          verified: verifiedOnly ? true : null,
          sort,
          limit: SUPPLIER_PAGE_SIZE,
        });

        if (!isActive || latestRequestKeyRef.current !== requestKey) return;

        setItems(payload.items || []);
        setFilters(payload.filters || EMPTY_FILTERS);
        setNextCursor(payload.nextCursor || null);
        setHasMore(Boolean(payload.hasMore));
      } catch (fetchError) {
        if (!isActive || latestRequestKeyRef.current !== requestKey) return;
        setError("We couldn't load suppliers right now.");
      } finally {
        if (isActive && latestRequestKeyRef.current === requestKey) {
          setIsInitialLoading(false);
        }
      }
    };

    loadSuppliers();

    return () => {
      isActive = false;
    };
  }, [deferredQuery, selectedCategoryId, verifiedOnly, sort, reloadTick]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return;

    const requestKey = latestRequestKeyRef.current;
    setIsLoadingMore(true);
    setError(null);

    try {
      const payload = await fetchWelcomeSuppliers({
        query: deferredQuery,
        categoryId: selectedCategoryId || null,
        verified: verifiedOnly ? true : null,
        sort,
        cursor: nextCursor,
        limit: SUPPLIER_PAGE_SIZE,
      });

      if (latestRequestKeyRef.current !== requestKey) return;

      setItems((currentItems) => {
        const existingIds = new Set(currentItems.map((item) => item.id));
        const appendedItems = (payload.items || []).filter(
          (item) => !existingIds.has(item.id)
        );
        return [...currentItems, ...appendedItems];
      });
      setNextCursor(payload.nextCursor || null);
      setHasMore(Boolean(payload.hasMore));
      setFilters(payload.filters || EMPTY_FILTERS);
    } catch (fetchError) {
      if (latestRequestKeyRef.current !== requestKey) return;
      setError("We couldn't load more suppliers right now.");
    } finally {
      if (latestRequestKeyRef.current === requestKey) {
        setIsLoadingMore(false);
      }
    }
  };

  const selectedCategory =
    filters.categories.find((category) => category.id === selectedCategoryId) ||
    filters.rootCategories.find((category) => category.id === selectedCategoryId) ||
    null;
  const hasActiveFilters =
    Boolean(deferredQuery) || Boolean(selectedCategoryId) || verifiedOnly;

  const resetFilters = () => {
    setSearchInput("");
    setSelectedCategoryId("");
    setVerifiedOnly(false);
    setSort("rating_desc");
  };

  return (
    <div className="w-full">
      <section className="border-b border-border/70 bg-background">
        <div className="container mx-auto px-4 py-5 sm:px-6">
          <div className="card-surface overflow-hidden p-4 sm:p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_220px]">
              <div className="space-y-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search suppliers by company name"
                    className="h-11 rounded-lg border-border bg-muted/50 pl-11 text-sm shadow-sm focus-visible:bg-background"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                  <span>
                    {isInitialLoading
                      ? "Loading suppliers..."
                      : `${formatCompactCount(items.length)} suppliers loaded`}
                  </span>
                  {deferredQuery ? (
                    <>
                      <span className="h-1 w-1 rounded-full bg-border" />
                      <span>Search: {deferredQuery}</span>
                    </>
                  ) : null}
                  {hasActiveFilters ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={resetFilters}
                      className="h-6 rounded px-2 text-muted-foreground hover:text-foreground"
                    >
                      Reset all
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Sort by
                </p>
                <Select
                  value={sort}
                  onValueChange={(value) => setSort(value as WelcomeSupplierSort)}
                >
                  <SelectTrigger className="h-11 rounded-lg bg-muted/40">
                    <SelectValue placeholder="Sort suppliers" />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                Popular supplier sectors
              </div>
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                <Button
                  type="button"
                  variant={!selectedCategoryId ? "default" : "outline"}
                  onClick={() => setSelectedCategoryId("")}
                  className="rounded-full whitespace-nowrap"
                >
                  All suppliers
                </Button>
                {filters.rootCategories.map((category) => (
                  <Button
                    key={category.id}
                    type="button"
                    variant={
                      selectedCategoryId === category.id ? "default" : "outline"
                    }
                    onClick={() =>
                      setSelectedCategoryId((currentValue) =>
                        currentValue === category.id ? "" : category.id
                      )
                    }
                    className="rounded-full whitespace-nowrap"
                  >
                    {category.name}
                    <span className="ml-2 rounded-full bg-background/70 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      {formatCompactCount(category.supplierCount)}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Category
                </p>
                <Select
                  value={selectedCategoryId || "all"}
                  onValueChange={(value) =>
                    setSelectedCategoryId(value === "all" ? "" : value)
                  }
                >
                  <SelectTrigger className="h-10 rounded-lg bg-muted/40">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">All categories</SelectItem>
                    {filters.categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.displayName} ({formatCompactCount(category.supplierCount)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                  Supplier status
                </p>
                <Button
                  type="button"
                  variant={verifiedOnly ? "default" : "outline"}
                  onClick={() => setVerifiedOnly((currentValue) => !currentValue)}
                  className="h-10 w-full justify-start rounded-lg"
                >
                  <BadgeCheck className="mr-2 h-4 w-4" />
                  Verified suppliers only
                </Button>
              </div>
            </div>

            {hasActiveFilters ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {deferredQuery ? (
                  <Badge variant="secondary">Search: {deferredQuery}</Badge>
                ) : null}
                {selectedCategory ? (
                  <Badge variant="secondary">
                    Category: {selectedCategory.name}
                  </Badge>
                ) : null}
                {verifiedOnly ? (
                  <Badge variant="secondary">Verified only</Badge>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-7 gap-1 rounded-full px-3 text-muted-foreground hover:text-foreground"
                >
                  <FilterX className="h-3.5 w-3.5" />
                  Clear filters
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-5 sm:px-6">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                Browse featured suppliers
              </h1>
              <p className="text-sm text-muted-foreground">
                Supplier discovery stays responsive with server-side search,
                filters, and cursor pagination.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              Open a profile to inspect company details and live product listings.
            </p>
          </div>

          {error ? (
            <div className="card-surface mb-4 border-destructive/20 bg-destructive/5 p-4">
              <div className="flex flex-col gap-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">
                    Suppliers are unavailable right now
                  </h2>
                  <p className="mt-1 text-muted-foreground">{error}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setReloadTick((currentValue) => currentValue + 1)}
                  className="text-foreground hover:bg-background hover:text-foreground"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : null}

          {isInitialLoading ? (
            <SupplierGridSkeleton />
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {items.map((item) => (
                <SupplierCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="card-surface px-6 py-12 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Building2 className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-lg font-semibold text-foreground">
                No suppliers matched these filters
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a broader search, switch categories, or clear the current
                filters to continue browsing supplier profiles.
              </p>
              {hasActiveFilters ? (
                <div className="mt-5">
                  <Button type="button" variant="outline" onClick={resetFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          <div className="mt-6 flex flex-col items-center gap-3">
            {hasMore ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="h-10 gap-2 text-foreground hover:border-slate-300 hover:bg-slate-100 hover:text-foreground dark:hover:border-slate-700 dark:hover:bg-slate-800"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading more suppliers
                  </>
                ) : (
                  <>
                    Load more suppliers
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : !isInitialLoading && items.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                You&apos;ve reached the end of the available supplier profiles.
              </p>
            ) : null}
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            <div className="card-surface p-5">
              <div className="icon-box h-10 w-10">
                <Factory className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-base font-semibold text-foreground">
                Company-first sourcing
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Compare suppliers by capabilities, trust markers, and category
                strength before you open product-level discussions.
              </p>
            </div>

            <div className="card-surface p-5">
              <div className="icon-box h-10 w-10">
                <BadgeCheck className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-base font-semibold text-foreground">
                Verified profile signals
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Ratings, response rate, listed products, and destination markets
                give buyers a fast trust read before inquiry.
              </p>
            </div>

            <div className="card-surface p-5">
              <div className="icon-box h-10 w-10">
                <Globe2 className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-base font-semibold text-foreground">
                Built for scale
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Filters and pagination run on the server so the directory stays
                usable even as supplier volume keeps growing.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function WelcomeSuppliersPage() {
  return (
    <Suspense
      fallback={
        <div className="w-full">
          <section className="border-b border-border/60 bg-background">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <div className="card-surface p-3 sm:p-4">
                <div className="h-11 rounded-lg bg-muted/70 animate-pulse" />
                <div className="mt-2 h-4 w-40 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </section>

          <section className="bg-slate-50 dark:bg-slate-900">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <SupplierGridSkeleton />
            </div>
          </section>
        </div>
      }
    >
      <WelcomeSuppliersPageContent />
    </Suspense>
  );
}
